import { useCallback, useContext } from 'react'
import { userIdentifier } from '..'
import { StoreContext } from '../../store'
import { useMutation } from '@apollo/client'
import { useParams } from 'react-router'
import { tokenService } from '../../services/token'
import { ADD_INTERIOR_SELECTION } from '../../graphql/interiorBrochure'
import useClientByName from '../../hooks/clientByName'

/**
 *
 * @returns
 */
function useBrochureParameters() {
  const { prospect, anonymousProspect, selection } = useContext(StoreContext)
  const { clientName } = useParams()
  const prospectToken = tokenService.token()?.value

  const { client } = useClientByName(clientName)

  return {
    prospect,
    anonymousProspect,
    selection: selection.interiorDesignSelections,
    client,
    clientName,
    prospectToken,
  }
}

/**
 * `generateBrochureUrl` returns a truss brochure URL
 * @param {object} params Brochure params
 * @returns {string} brochure URL
 */
function generateBrochureUrl({
  prospect,
  anonymousProspect,
  clientName,
  selectionId,
  interiorId,
}) {
  const user = userIdentifier(prospect, anonymousProspect)
  const prospectId = encodeURIComponent(user)

  if (interiorId) {
    return `/client/${clientName}/interior/${interiorId}/brochure?prospectIdentifier=${prospectId}&id=${selectionId}`
  } else {
    return `/client/${clientName}/interiors/brochure?prospectIdentifier=${prospectId}&id=${selectionId}`
  }
}

function useSaveBrochure() {
  const [trigger] = useMutation(ADD_INTERIOR_SELECTION)
  return useCallback(
    async ({ clientName, prospectIdentifier, selection, source }) => {
      return await trigger({
        variables: {
          clientName,
          input: {
            prospectIdentifier,
            selection,
            source,
          },
        },
      })
    },
    [trigger]
  )
}

async function createBrochure(
  path,
  { client, clientName, prospect, interiorName }
) {
  const url = `${process.env.REACT_APP_INGLENOOK_ENGINE}/api/email/interior`
  const dst = `${process.env.REACT_APP_TRUSS}${path}`

  return await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      dst,
      selector: '[data-brochure-complete]',
      clientName,
      companyName: client.name,
      companyAddress: client.address,
      companyContact: client.contact,
      companyEmail: client.email,
      interiorName,
      prospect: {
        name: prospect?.name,
        email: prospect?.email,
      },
    }),
  }).then((res) => {
    // Throw an error on 400s, 500s
    if (!res.ok) {
      throw new Error(res.statusText)
    }

    return res
  })
}

export function useEmailBrochure() {
  const saveBrochure = useSaveBrochure()
  const { selection, client, clientName, prospect, anonymousProspect } =
    useBrochureParameters()

  return useCallback(
    async ({ prospect: customProspect, interiorId, interiorName }) => {
      const prospectToUse = customProspect ?? prospect

      const prospectIdentifier = `prospect-${userIdentifier(
        prospectToUse,
        anonymousProspect
      )}`

      const result = await saveBrochure({
        clientName,
        prospectIdentifier,
        selection,
        source: 'truss',
      })

      const selectionId = result.data.addInteriorSelection

      const path = generateBrochureUrl({
        prospect: prospectToUse,
        anonymousProspect,
        clientName,
        interiorId,
        selectionId,
      })

      await createBrochure(path, {
        client,
        clientName,
        interiorName,
        prospect: prospectToUse,
      })
    },
    [selection, client, clientName, prospect, anonymousProspect, saveBrochure]
  )
}
